import React, { useEffect, useLayoutEffect } from "react";

import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";

import { useRootAppStore } from "@hooks";
import { redirect } from "@state";
import { MobilePlatform } from "@state/stores/types";

import LoaderSpinner from "@components-shared/LoaderSpinner";
import SupportChatArea from "./SupportChatArea";

import { getCurrentCulture, setTabTitle } from "@utils";
import { DefaultPath } from "@utils/common/defaultRoutePathsEnum";

import { ChatEntryPoint } from "@data-types/chat";
import ErrorBoundaryWithPropagation from "@components/boundary/ErrorBoundaryWithPropagation";
import InfoMessageComponent from "@components-shared/InfoMessageComponent";
import { workingHours } from "@features/chat/shared";
import { useScrollRestoration } from "@hooks/useScrollRestoration";
import { ConfigType } from "@data-types/use-scroll-restoration/useScrollRestoration";

export default observer(function ChatSupportPage() {
	const { chatViewStore, diagnosticsProvider, userAuthStore, pageStore } =
		useRootAppStore<MobilePlatform>();
	const { t } = useTranslation();

	useScrollRestoration({ type: ConfigType.Basic });

	useLayoutEffect(() => {
		pageStore.setSubPageHeader({
			title: t("CHAT.SUPPORT.TITLE"),
			backButton: () =>
				redirect(`/${getCurrentCulture()}/${DefaultPath.HOME}`),
		});
	}, []);

	useEffect(() => {
		setTabTitle(t("CHAT.SUPPORT.TITLE"));
		chatViewStore.toggleChatWidget(false);
		chatViewStore.onInitialize(ChatEntryPoint.SUPPORT, null);

		return () => {
			chatViewStore.closeSupportChat();
		};
	}, []);

	if (!WEBPACK_IS_SUPPORT_CHAT_AVAILABLE || !userAuthStore.isLoggedIn) {
		return null;
	}

	return (
		<ErrorBoundaryWithPropagation
			componentName={"Mobile/ChatSupportPage.tsx"}
		>
			<div className="container container--grow">
				{!chatViewStore.chatBoxVisible && (
					<LoaderSpinner
						isMobile
						isLoading={chatViewStore.chatLoader.isLoading}
					/>
				)}

				<div className="subheader subheader--scrollable--inverted subheader--support">
					{/* HEADER TODO: where does this info button go now? */}
					<div className="subheader--support__inner">
						<div
							onClick={() =>
								chatViewStore.toggleSupportChatPopup(true)
							}
						>
							<i className="u-icon u-icon--xmed u-icon--info-circle" />
						</div>
					</div>

					<InfoMessageComponent
						type="note"
						size="sml"
						customIcon="u-icon--big u-icon--working-hours u-color--sent--note"
						addClassName="subheader--support__message"
						message={t("CHAT.SUPPORT.WORKING_HOURS", {
							"0": workingHours.from,
							"1": workingHours.to,
						})}
					/>
				</div>

				<div className="chat--support">
					<SupportChatArea
						chatViewStore={chatViewStore}
						diagnosticsProvider={diagnosticsProvider}
					/>
				</div>
			</div>
		</ErrorBoundaryWithPropagation>
	);
});
